import { QueryClient } from '@tanstack/react-query';
import throttle from 'lodash/throttle';

import type {
  CaseDataEntityItem,
  CaseDataTuple,
} from '@app/components/Chats/details-nav/sections/fnol/use-fnol-data-query';
import { QUERY_KEYS } from '@app/constants/service';
import {
  type CaseVisibilityScope,
  UNSCOPED_CASE_VISIBILITY,
} from '@app/helpers/case-visibility';
import {
  getWebSocketPayloadArray,
  parseEventPayload,
} from '@app/helpers/websocket-event-payload';
import { fetchCases } from '@app/services/case/cases';
import { Job } from '@app/types/api/job';
import { User } from '@app/types/api/user';
import { Queue } from '@app/types/queue';

import {
  findExistingJobInCache,
  handleReactQueryJobCacheCustomerNames,
  handleReactQueryJobCacheItem,
} from '../cache/cases/react-query-job-cache';
import * as constants from '../constants/websocket';

/** First element of queue list query key: ['queue', { id: string }] */
type QueueListQueryKeyFirst = [string, { id?: string }];

function getQueueIdFromQueueListQueryKey(
  queryKey: readonly unknown[],
): string | undefined {
  const [key] = queryKey;
  if (!Array.isArray(key) || (key as unknown[])[0] !== 'queue')
    return undefined;
  const queueKey = key as QueueListQueryKeyFirst;
  const id = queueKey[1]?.id;
  const hasId = id !== undefined && id !== null && id !== '';
  return hasId ? id : undefined;
}

function isQueueListQueryWithId(query: {
  queryKey: readonly unknown[];
}): boolean {
  return getQueueIdFromQueueListQueryKey(query.queryKey) !== undefined;
}

function pageContainsJob(
  page: { queueItems?: Array<{ caseId?: string; id?: string }> },
  jobId: string,
): boolean {
  return (
    page.queueItems?.some(
      (item) => item.caseId === jobId || item.id === jobId,
    ) ?? false
  );
}

/**
 * Collects all queue IDs that should be invalidated for a job update:
 * - The job's current queueId from the payload (when case is added/moved)
 * - The job's previous queueId from cache (when case is removed or moved)
 *   from both the cases (inbox) cache and the queue list cache
 */
function getQueueIdsToInvalidateForJob(
  queryClient: QueryClient,
  jobId: string,
  payloadQueueId: string | undefined,
): Set<string> {
  const queueIds = new Set<string>();
  const add = (id: string | undefined | null) => {
    if (typeof id === 'string' && id) queueIds.add(id);
  };

  add(payloadQueueId);

  const existingJob = findExistingJobInCache(queryClient, jobId);
  const existingQueueId = existingJob?.queueId;
  if (existingQueueId) {
    add(
      typeof existingQueueId === 'string'
        ? existingQueueId
        : (existingQueueId as { _id?: string })?._id,
    );
  }

  const cache = queryClient.getQueryCache();
  const queueListQueries = cache.getAll().filter(isQueueListQueryWithId);
  for (const query of queueListQueries) {
    const queueId = getQueueIdFromQueueListQueryKey(query.queryKey);
    if (queueId) {
      const data = queryClient.getQueryData(query.queryKey) as
        | {
            pages?: Array<{
              queueItems?: Array<{ caseId?: string; id?: string }>;
            }>;
          }
        | undefined;
      const hasJob = data?.pages?.some((page) => pageContainsJob(page, jobId));
      if (hasJob) queueIds.add(queueId);
    }
  }

  return queueIds;
}

/** Shape of queue list cache data (infinite query pages) */
interface QueueListCachePage {
  queueItems?: Array<{ caseId?: string; id?: string; [key: string]: unknown }>;
  stats?: { itemCount?: number; [key: string]: unknown };
}
interface QueueListCacheData {
  pages?: QueueListCachePage[];
}

/**
 * Removes a job from a queue's list cache without refetching.
 * Updates all query variants for that queue (different pageSize/searchText).
 * Gives instant UI update when a case is removed from a queue.
 */
function removeJobFromQueueListCache(
  queryClient: QueryClient,
  queueId: string,
  jobId: string,
): void {
  const cache = queryClient.getQueryCache();
  const queueListQueries = cache
    .getAll()
    .filter(
      (query) => getQueueIdFromQueueListQueryKey(query.queryKey) === queueId,
    );

  for (const query of queueListQueries) {
    queryClient.setQueryData(query.queryKey, (oldData: unknown) => {
      const data = oldData as QueueListCacheData | undefined;
      if (!data?.pages) return oldData;

      const updatedPages = data.pages.map((page) => {
        const items = page.queueItems?.filter(
          (item) => item.caseId !== jobId && item.id !== jobId,
        );
        const removed = (page.queueItems?.length ?? 0) - (items?.length ?? 0);
        const stats =
          removed > 0 &&
          page.stats?.itemCount !== undefined &&
          page.stats?.itemCount !== null
            ? {
                ...page.stats,
                itemCount: Math.max(0, page.stats.itemCount - removed),
              }
            : page.stats;
        return {
          ...page,
          queueItems: items ?? page.queueItems,
          ...(stats !== undefined ? { stats } : {}),
        };
      });

      return { ...data, pages: updatedPages };
    });
  }
}

interface MessageEvent {
  detail?: {
    payload?: Array<{
      name: string;
      payload: unknown;
      userId?: string;
    }>;
  };
}

function setFnolDataFromJob(
  jobData: Record<string, unknown>,
  queryClient: QueryClient,
): void {
  const jobId = jobData?._id ?? jobData?.id;
  if (typeof jobId !== 'string' || !jobId || !('fnolData' in jobData)) return;

  const queryKey = QUERY_KEYS.FNOL_DATA(jobId);
  if (queryClient.getQueryData(queryKey) === undefined) return;

  const fnolData = jobData.fnolData as
    | { entities?: Record<string, CaseDataEntityItem> }
    | undefined;
  queryClient.setQueryData<CaseDataTuple[]>(
    queryKey,
    Object.entries(fnolData?.entities ?? {}),
  );
}

const updateJobEvent = (
  data: unknown,
  queryClient: QueryClient,
  eventName: string,
  userId: string,
  permissions: string[],
  unaddressedEnabled: boolean,
  suppressionEnabled: boolean,
  caseVisibility: CaseVisibilityScope,
) => {
  // Check if data is a partial job with only _id
  const jobData = data as Record<string, unknown>;
  if (jobData && Object.keys(jobData).length === 1 && '_id' in jobData) {
    // Partial update - fetch just this one job by ID, then update all cache entries
    const jobId = jobData._id as string;

    // Refetch FNOL data for job:update
    if (eventName === constants.UPDATED_JOB) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FNOL_DATA(jobId) });
    }

    // Fetch the job data and update all caches
    fetchCases({ jobIds: [jobId] }, 1).then((jobListData) => {
      if (jobListData?.length > 0) {
        const [updatedJob] = jobListData;
        // Use the existing cache update handler to update all joblist caches
        handleReactQueryJobCacheItem(
          updatedJob as Job,
          eventName,
          queryClient,
          userId,
          permissions,
          unaddressedEnabled,
          suppressionEnabled,
          caseVisibility,
        );
      }
    });
    return;
  }

  setFnolDataFromJob(jobData, queryClient);

  // Only handle React Query cache updates - Redux handled by event-processor.js
  handleReactQueryJobCacheItem(
    data as Job,
    eventName,
    queryClient,
    userId,
    permissions,
    unaddressedEnabled,
    suppressionEnabled,
    caseVisibility,
  );
};

interface NewMessageEventData {
  job?: Job;
  message?: unknown;
  newMessage?: unknown;
}

const handleNewMessageEvent = (
  data: unknown,
  queryClient: QueryClient,
  eventName: string,
  userId: string,
  permissions: string[],
  unaddressedEnabled: boolean,
  suppressionEnabled: boolean,
  caseVisibility: CaseVisibilityScope,
) => {
  const messageData = data as NewMessageEventData;
  const { job } = messageData;

  // Only handle React Query cache updates for job data
  // Chat message cache updates are handled by event-processor.js
  // User model processing is handled by handleSimplifiedCaseCacheItem
  if (job) {
    handleReactQueryJobCacheItem(
      job,
      eventName,
      queryClient,
      userId,
      permissions,
      unaddressedEnabled,
      suppressionEnabled,
      caseVisibility,
    );
  }
};

const handleModifiedUser = (data: unknown, queryClient: QueryClient) => {
  // Only handle React Query cache updates - Redux handled by event-processor.js
  handleReactQueryJobCacheCustomerNames(data as User, queryClient);
};

// Wrapper for events that have job data nested in the payload
const handleJobUpdateEvent = (
  data: unknown,
  queryClient: QueryClient,
  eventName: string,
  userId: string,
  permissions: string[],
  unaddressedEnabled: boolean,
  suppressionEnabled: boolean,
  caseVisibility: CaseVisibilityScope,
) => {
  const jobData = data as { job?: Job };
  if (jobData?.job) {
    updateJobEvent(
      jobData.job,
      queryClient,
      eventName,
      userId,
      permissions,
      unaddressedEnabled,
      suppressionEnabled,
      caseVisibility,
    );
  }
};

type JobEventHandler = (
  data: unknown,
  queryClient: QueryClient,
  eventName: string,
  userId: string,
  permissions: string[],
  unaddressedEnabled: boolean,
  suppressionEnabled: boolean,
  caseVisibility: CaseVisibilityScope,
) => void;

const jobEventMap: Record<string, JobEventHandler> = {};

jobEventMap[constants.UPDATED_USER] = handleModifiedUser;
jobEventMap[constants.UPDATED_JOB] = updateJobEvent;
// NOTE: NEW_JOB events are NOT handled here because they lack initial message data.
// The job is only added to the cache with complete data when the `chat:refresh_messages` event arrives.
// Events that update job properties
jobEventMap[constants.NEW_MESSAGE] = handleNewMessageEvent;
jobEventMap[constants.DISMISS_NEEDS_ACTION] = handleJobUpdateEvent;
jobEventMap[constants.DISMISS_NEEDS_ATTENTION_REPLY] = handleJobUpdateEvent;
jobEventMap[constants.DISMISS_NEEDS_ATTENTION] = handleJobUpdateEvent;
jobEventMap[constants.UPDATE_MESSAGE] = handleJobUpdateEvent;
jobEventMap[constants.INLINE_MESSAGE_UPDATE] = handleJobUpdateEvent;
jobEventMap[constants.UPDATED_NEEDS_ACTION] = handleJobUpdateEvent;
jobEventMap[constants.UPDATED_SURVEY_SCORE] = handleJobUpdateEvent;

export const JOB_RELATED_EVENTS = new Set([
  constants.UPDATED_JOB,
  constants.UPDATED_USER,
  // Events that update job properties and need cache updates
  constants.NEW_MESSAGE,
  constants.DISMISS_NEEDS_ACTION,
  constants.DISMISS_NEEDS_ATTENTION_REPLY,
  constants.DISMISS_NEEDS_ATTENTION,
  constants.UPDATE_MESSAGE,
  constants.INLINE_MESSAGE_UPDATE,
  constants.UPDATED_NEEDS_ACTION,
  constants.UPDATED_SURVEY_SCORE,
]);

export const jobEventProcessor = (
  queryClient: QueryClient,
  userId: string,
  permissions: string[],
  unaddressedEnabled = false,
  suppressionEnabled = false,
  caseVisibility: CaseVisibilityScope = UNSCOPED_CASE_VISIBILITY,
) => {
  // Create throttled invalidation function (throttled to once per second)
  const throttledInvalidateQueues = throttle(
    () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNED_QUEUES] });
    },
    2000, // Throttle to once every 2 seconds
    { leading: true, trailing: true }, // Execute on first and last call
  );

  // Invalidate a specific queue's list/stats so the queue page refetches (avoids waiting for refetchInterval)
  // Only used when a job is *added* to a queue (remove uses direct cache update)
  const throttledInvalidateQueueById = throttle(
    (queueId: string) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUEUE(queueId)] });
    },
    150,
    { leading: true, trailing: true },
  );

  return (messageEvent: MessageEvent) => {
    const payload = getWebSocketPayloadArray(messageEvent);
    if (!payload) return;

    const jobEvents = payload.filter((event) =>
      JOB_RELATED_EVENTS.has(event.name),
    );

    const queueIdsToInvalidate = new Set<string>();

    jobEvents.forEach((event) => {
      const eventHandler = jobEventMap[event.name];
      if (eventHandler) {
        try {
          const data = parseEventPayload(event.payload);
          const jobId =
            (data as { _id?: string; job?: { _id?: string } })?._id ??
            (data as { job?: { _id?: string } })?.job?._id;
          const payloadObj =
            data && typeof data === 'object'
              ? (data as Record<string, unknown>)
              : {};
          const rawQueueId = payloadObj?.queueId;
          const payloadQueueId =
            typeof rawQueueId === 'string'
              ? rawQueueId
              : (rawQueueId as { _id?: string })?._id;
          if (jobId) {
            const affectedQueueIds = getQueueIdsToInvalidateForJob(
              queryClient,
              jobId,
              payloadQueueId as string | undefined,
            );
            affectedQueueIds.forEach((queueId) => {
              if (queueId !== payloadQueueId) {
                // Job left this queue: update cache directly (instant), then invalidate so list stays correct
                removeJobFromQueueListCache(queryClient, queueId, jobId);
              }
              // Invalidate every affected queue (previous and new) so reassigning between queues
              // refetches both the queue the job left and the one it joined
              queueIdsToInvalidate.add(queueId);
            });
          } else if (typeof payloadQueueId === 'string') {
            queueIdsToInvalidate.add(payloadQueueId);
          }
          eventHandler(
            data,
            queryClient,
            event.name,
            userId,
            permissions,
            unaddressedEnabled,
            suppressionEnabled,
            caseVisibility,
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            '[LiveUpdate] job event handler error',
            event.payload,
            err,
          );
        }
      }
    });

    // Invalidate queue list for each affected queue so queue page updates without waiting for refetchInterval (15s)
    queueIdsToInvalidate.forEach((queueId) =>
      throttledInvalidateQueueById(queueId),
    );

    // Invalidate assigned-queues query to refresh queue counts in sidebar
    if (jobEvents.length > 0) {
      const assignedQueues = queryClient.getQueryData<Queue[]>([
        QUERY_KEYS.ASSIGNED_QUEUES,
      ]);
      if (assignedQueues && assignedQueues.length > 0) {
        throttledInvalidateQueues();
      }
    }
  };
};
